import { isEqual } from 'lodash'
import { normalize, schema } from 'normalizr'
import { Reducer, Action } from 'redux'
import { CARRIER_MATCHES_ACTION_TYPES } from 'utils/actions/carrierMatches'
import {
  ICarrierMatchesState,
  IMatch,
  ICarrierMatchesFilters,
} from 'apps/booking/types/carrierMatches'

interface ICarrierMatchesRequestAction extends Action<string> {
  payload: {
    requestData: {
      params: Partial<ICarrierMatchesFilters>
    }
  }
}

interface ICarrierMatchesSuccessAction extends Action<string> {
  payload: {
    matches: IMatch[]
    totalCount: number
    requestData: {
      params: Partial<ICarrierMatchesFilters>
    }
  }
}

interface ICarrierMatchesFailureAction extends Action<string> {
  error: string
  payload: {
    requestData: {
      params: Partial<ICarrierMatchesFilters>
    }
  }
}

type ICarrierMatchesAction =
  | ICarrierMatchesRequestAction
  | ICarrierMatchesSuccessAction
  | ICarrierMatchesFailureAction

const {
  CARRIER_MATCHES_FAILURE,
  CARRIER_MATCHES_REQUEST,
  CARRIER_MATCHES_SUCCESS,
} = CARRIER_MATCHES_ACTION_TYPES

const defaultState = {
  loading: false,
  error: '',
  data: {},
  totalCount: 0,
  currentFilters: {},
}

const matchesSchema = new schema.Entity('matches', {}, { idAttribute: 'carrierId' })

const carrierMatchesReducer: Reducer<ICarrierMatchesState, ICarrierMatchesAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case CARRIER_MATCHES_REQUEST: {
      const {
        payload: {
          requestData: { params },
        },
      } = action as ICarrierMatchesRequestAction
      return {
        ...state,
        loading: true,
        error: '',
        data: {},
        totalCount: 0,
        currentFilters: params,
      }
    }
    case CARRIER_MATCHES_SUCCESS: {
      const {
        payload: {
          matches,
          requestData: { params },
          totalCount,
        },
      } = action as ICarrierMatchesSuccessAction
      // To prevent a possible race condition where multiple fast clicks over shipments don't overwrite each other
      // we check whether the filters of the last request match with whatever was resolved in the success/fail action
      // if they match, we update the data, if not we ignore it (since we can't cancel inflight requests yet)
      if (isEqual(params, state.currentFilters)) {
        const normalizedData = normalize(matches, [matchesSchema])
        return {
          ...state,
          loading: false,
          data: normalizedData,
          totalCount,
        }
      }
      return state
    }
    case CARRIER_MATCHES_FAILURE: {
      const {
        error,
        payload: {
          requestData: { params },
        },
      } = action as ICarrierMatchesFailureAction
      if (isEqual(params, state.currentFilters)) {
        return {
          ...state,
          loading: false,
          error,
        }
      }
      return state
    }
    default:
      return state
  }
}

export default carrierMatchesReducer
